import { z } from 'zod';

/**
 * Scheduled jobs (docs/scheduled-jobs-and-report-extras-plan.md, Part A).
 * All times are academy time (IST). The server checks once an hour, so a
 * job's time is an hour of the day.
 */

export const JOB_FREQUENCIES = ['hourly', 'daily', 'weekly', 'monthly'] as const;
export type JobFrequency = (typeof JOB_FREQUENCIES)[number];

export interface JobSchedule {
  frequency: JobFrequency;
  /** 0–23 (IST); unused for hourly jobs. */
  hour: number | null;
  /** 0 = Sunday … 6 = Saturday; weekly jobs only. */
  weekday: number | null;
  /** 1–28; monthly jobs only. */
  dayOfMonth: number | null;
}

export const jobScheduleUpdateSchema = z
  .object({
    enabled: z.boolean().optional(),
    frequency: z.enum(JOB_FREQUENCIES).optional(),
    hour: z.number().int().min(0).max(23).optional(),
    weekday: z.number().int().min(0).max(6).optional(),
    dayOfMonth: z.number().int().min(1).max(28).optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change');
export type JobScheduleUpdate = z.infer<typeof jobScheduleUpdateSchema>;

export const jobRunNowSchema = z
  .object({
    confirm: z.boolean().optional(),
    /**
     * Daily jobs that send messages, pressed before their time: still do
     * today's usual run ("keep", the default), or skip it ("skip").
     */
    todayRun: z.enum(['keep', 'skip']).optional(),
  })
  .strict();

/** "Skip next run", with an optional reason shown in the history. */
export const jobSkipSchema = z.object({ reason: z.string().trim().max(200).optional() }).strict();

export type JobRunStatus = 'running' | 'ok' | 'partial' | 'skipped' | 'failed' | 'gave_up';

export interface ScheduledJobRunDto {
  id: string;
  slot: string;
  status: JobRunStatus;
  attempt: number;
  trigger: 'schedule' | 'retry' | 'manual';
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  summary: string | null;
  error: string | null;
  startedByName: string | null;
}

/** What the job page shows as the job's state right now. */
export type JobState = 'ok' | 'skipped' | 'running' | 'partial' | 'retrying' | 'failed' | 'waiting' | 'off' | 'never';

export interface ScheduledJobDto {
  key: string;
  label: string;
  description: string;
  /** Plain words: what people would notice, and what happens next. */
  ifItFails: string;
  group: string;
  enabled: boolean;
  canDisable: boolean;
  /** Sends emails or notifications — "Run now" asks first. */
  sendsMessages: boolean;
  schedule: JobSchedule;
  allowedFrequencies: JobFrequency[];
  /** Hours the job may be set to, inclusive. */
  allowedHours: { from: number; to: number };
  /** e.g. "The day follows the report settings" when the day isn't editable here. */
  scheduleNote: string | null;
  /** A switch elsewhere that must also be on, e.g. a feature. */
  needs: { label: string; on: boolean } | null;
  state: JobState;
  lastRun: ScheduledJobRunDto | null;
  lastSuccessAt: string | null;
  nextRunAt: string | null;
  /** Its time has passed today (or this week/month) and it hasn't run yet — it goes at the next hourly check. */
  dueNow: boolean;
  /** Daily jobs: today's run is still ahead (and not skipped), so "Run now" can ask about it. */
  todayRunAt: string | null;
  /** What "Skip next run" would skip; null while the job is off. */
  skipTarget: { runAt: string; warning: string | null } | null;
  /** The next run is skipped ("Skip next run"); Undo removes it. */
  nextSkip: { runAt: string; byName: string | null; reason: string | null } | null;
}

export interface ScheduledJobsResponse {
  jobs: ScheduledJobDto[];
  heartbeat: { lastTickAt: string | null; healthy: boolean };
  email: EmailStatusDto;
}

export type EmailFailureKind = 'not_set_up' | 'temporary' | 'rejected';

/** The Email card in System → Settings, and the email banner on Scheduled jobs. */
export interface EmailStatusDto {
  /** "Send automatic emails" switch. Sign-in and account emails always try. */
  automaticEmailsEnabled: boolean;
  /** Whether emails are getting through, going by the most recent attempt. */
  health: 'working' | 'not_working' | 'unknown';
  lastOkAt: string | null;
  lastFailureAt: string | null;
  lastFailureKind: EmailFailureKind | null;
  /** Plain words, e.g. "the email service says the sending domain isn't verified". */
  problem: string | null;
}
